import { AnnouncerPackFactory, AnnouncerPackType } from "../../../core/engine/packs/announcers/AnnouncerPackFactory";
import { AnnouncerPackRegistry } from "../../../core/engine/packs/announcers/AnnouncerPackRegistry";
import { GameMessageEvent, GameState } from "../../../core/protocol/types";
import { IAudioManager } from "../../../core/engine/types/audio-manager";
import { MessageAnimationContext } from "../../components/MultiPlayer/messageAnimations";

/**
 * Manages announcer packs and handles game message events
 * Integrates with runtime to process announcer events
 */
export class AnnouncerManager {
  private readonly packFactory: AnnouncerPackFactory;
  private readonly packRegistry: AnnouncerPackRegistry;
  private onMessageCallback?: (message: GameMessageEvent) => void;

  constructor(audioManager: IAudioManager) {
    this.packFactory = new AnnouncerPackFactory();
    this.packFactory.initialize(audioManager);
    this.packRegistry = new AnnouncerPackRegistry(this.packFactory);
    this.preloadInitialPacks();
  }

  private preloadInitialPacks(): void {
    this.packFactory.preloadPack("default");
    const savedPack = this.getSavedPackType();
    if (savedPack !== "default") {
      this.packFactory.preloadPack(savedPack);
    }
  }

  private getSavedPackType(): AnnouncerPackType {
    try {
      const stored = localStorage.getItem("fragAnnouncerPack");
      return stored ? (JSON.parse(stored) as AnnouncerPackType) : "default";
    } catch {
      return "default";
    }
  }

  private isAnnouncerPackType(type: string): type is AnnouncerPackType {
    return type === "default" || type === "fireworks" || type === "kit-kit-miau-miau";
  }

  setFragAnnouncerPack(type: string): void {
    localStorage.setItem("fragAnnouncerPack", JSON.stringify(type));
    if (this.isAnnouncerPackType(type)) {
      this.packFactory.preloadPack(type);
    }
  }

  handleGameMessages(
    messages: GameMessageEvent[],
    gameState: GameState,
    localConnectionId: string
  ): void {
    const localTeam = this.resolveLocalTeam(gameState, localConnectionId);
    messages.forEach(message => {
      // Get appropriate pack for this event type's category
      const pack = this.packRegistry.getPackForEventType(message.type);
      const context: MessageAnimationContext = {
        message,
        localConnectionId,
        localTeam,
        gameState,
        onComplete: () => {},
      };
      const handler = pack.getAnimationHandler(message.type, context);
      if (handler && this.onMessageCallback) {
        this.onMessageCallback(message);
      }
    });
  }

  private resolveLocalTeam(gameState: GameState, localConnectionId: string): number {
    if (!localConnectionId) {
      return 0;
    }
    const player = gameState.connections?.get(localConnectionId);
    return player?.team ?? 0;
  }

  setOnMessage(callback: (message: GameMessageEvent) => void): void {
    this.onMessageCallback = callback;
  }
}

